/**
 * Single source of truth for the routine channel-picker ↔ wire shape.
 *
 * The picker works in a flat, UI-friendly `RoutineChannelValue`
 * ({ slack?, gchat? }); the API/types speak in
 * `RoutineOutputChannelConfig[]` (one entry per active destination). These two
 * helpers are the only place that conversion happens, so the create flow, edit
 * flow, and any read-only summary all agree on the mapping.
 */

import type {
  RoutineOutputChannelConfig,
  RoutineChannelDestinationConfig,
} from '@/hooks/useRoutines';

/**
 * Flat, UI-friendly selection the `RoutineChannelPicker` is controlled by. The
 * in-app card is NOT here: it's always created (it's the alert record and the
 * "continue in chat" deep-link target), so the picker only chooses whether to
 * ALSO deliver to Slack / Google Chat.
 */
export interface RoutineChannelValue {
  slack?: { credential_id: string; channels: string[] };
  gchat?: { credential_id: string; spaces: string[] };
}

/**
 * Picker value → wire shape. A provider is only emitted when it actually has a
 * destination selected, so the backend never receives an empty `slack`/`gchat`
 * block (which would read as "configured but no targets"). The in-app card is
 * NOT emitted here — the platform always posts it regardless of this list, so an
 * empty selection yields an empty `channels` array (card-only delivery).
 */
export function toOutputChannels(value: RoutineChannelValue): {
  channels: RoutineOutputChannelConfig[];
} {
  const channels: RoutineOutputChannelConfig[] = [];

  if (value.slack && value.slack.channels.length > 0) {
    channels.push({
      channel: 'slack',
      config: { credential_id: value.slack.credential_id, channels: value.slack.channels },
    });
  }

  if (value.gchat && value.gchat.spaces.length > 0) {
    channels.push({
      channel: 'google_chat',
      config: { credential_id: value.gchat.credential_id, spaces: value.gchat.spaces },
    });
  }

  return { channels };
}

/** Human label per channel enum, for the compact read-only row summary. */
export const CHANNEL_LABELS: Record<RoutineOutputChannelConfig['channel'], string> = {
  slack: 'Slack',
  google_chat: 'Google Chat',
};

/** Labels for the distinct external channels, in stored order, deduped. */
function uniqueChannelLabels(
  channels: RoutineOutputChannelConfig[] | undefined
): string[] {
  return [...new Set((channels ?? []).map((c) => CHANNEL_LABELS[c.channel] ?? c.channel))];
}

// Oxford-comma list formatter, pinned to en-US so the row summary is
// locale-stable ("a", "a and b", "a, b, and c") across machines/CI.
const CHANNEL_LIST_FORMAT = new Intl.ListFormat('en-US', { type: 'conjunction' });

/**
 * Compact, human-readable summary of a routine's EXTERNAL destinations for the
 * read-only row, e.g. "Slack, Google Chat". Folds duplicates. Falls back to
 * "In-app only" when no external channel is selected — the in-app card is always
 * posted, so a routine is never truly without an alert destination.
 */
export function summarizeOutputChannels(
  channels: RoutineOutputChannelConfig[] | undefined
): string {
  const unique = uniqueChannelLabels(channels);
  return unique.length > 0 ? unique.join(', ') : 'In-app only';
}

/**
 * Full destination sentence for the channel-icons hover tooltip on the routine
 * row, e.g. "Alerts in Strive inbox", "Alerts in Strive inbox and Slack",
 * "Alerts in Strive inbox, Google Chat, and Slack". The in-app Strive Inbox is
 * always listed first (the platform always posts the card); external channels
 * follow in stored order, deduped, joined with an Oxford comma.
 *
 * @param channels - The routine's external output channels.
 *
 * @returns The human sentence naming every destination.
 */
export function describeOutputChannelsSentence(
  channels: RoutineOutputChannelConfig[] | undefined
): string {
  const parts = ['Strive inbox', ...uniqueChannelLabels(channels)];
  return `Alerts in ${CHANNEL_LIST_FORMAT.format(parts)}`;
}

/**
 * Wire shape → picker value. Inverse of {@link toOutputChannels}, used to seed
 * the edit picker from a routine's stored `output_channels.channels`. Tolerates
 * missing/partial config (defaults to empty arrays) so a malformed entry can't
 * throw the editor.
 */
export function fromOutputChannels(
  channels: RoutineOutputChannelConfig[] | undefined
): RoutineChannelValue {
  const value: RoutineChannelValue = {};

  for (const entry of channels ?? []) {
    const config: RoutineChannelDestinationConfig = entry.config ?? {};
    if (entry.channel === 'slack') {
      value.slack = {
        credential_id: config.credential_id ?? '',
        channels: config.channels ?? [],
      };
    } else if (entry.channel === 'google_chat') {
      value.gchat = {
        credential_id: config.credential_id ?? '',
        spaces: config.spaces ?? [],
      };
    }
  }

  return value;
}
